import React, {useEffect, useState} from 'react'
import {KTCardBody, KTIcon, toAbsoluteUrl} from '../../../_metronic/helpers'
import FilterUsers from './FilterUsers'
import {restRequest} from '../../../api-utils/apiQueries'
import {Link, useNavigate} from 'react-router-dom'
import Pagination from '../../common-function/Pagination'
import {ExportReportPayload, useExportReport} from '../../common-function/downloadExportFile'
import {
  appendDateFilterToBody,
  appendDateFilterToExport,
} from '../../common-function/adminDateFilterPayload'
import {useAdminListState} from '../../common-function/useAdminListState'
import ListFilterToolbar from '../../common-function/ListFilterToolbar'
import ListPageHeader from '../../common-function/ListPageHeader'
import {
  buildUserFilterSummary,
  getDefaultUserFilter,
  UserFilterState,
} from './userDateRange'
import 'react-toastify/dist/ReactToastify.css'
import {toast, ToastContainer} from 'react-toastify'
import {resolveMediaUrl} from '../../../api-utils/constantVariables'

const avatarFallback = toAbsoluteUrl('/media/user2.png')

const subscriptionBadge = (category?: string) => {
  if (!category || category === 'none') {
    return <span className='text-muted'>–</span>
  }
  const map: Record<string, {label: string; className: string}> = {
    free_trial_active: {label: 'Free Trial Active', className: 'badge-light-info'},
    subscribed: {label: 'Subscribed', className: 'badge-light-success'},
    inactive_subscription: {label: 'Inactive Subscription', className: 'badge-light-danger'},
  }
  const cfg = map[category]
  if (!cfg) return <span className='text-muted'>–</span>
  return <span className={`badge ${cfg.className}`}>{cfg.label}</span>
}

const profilePictureSrc = (path?: string | null): string => {
  if (!path || !String(path).trim()) return avatarFallback
  const url = resolveMediaUrl(path)
  return url || avatarFallback
}

const activityStatusBadge = (status?: string) => {
  if (!status) return <span className='text-muted'>–</span>
  const label =
    status === 'In Active'
      ? 'Inactive'
      : status === 'Subscribe'
      ? 'Driver Active'
      : status
  const className =
    status === 'In Active'
      ? 'badge-light-danger'
      : status === 'Trip Taken'
      ? 'badge-light-primary'
      : status === 'Subscribe' || status === 'Driver Active'
      ? 'badge-light-warning'
      : 'badge-light-secondary'
  return <span className={`badge ${className}`}>{label}</span>
}

const UserManagementWrapper = () => {
  const {runExport, exporting} = useExportReport()
  const [users, setUsers] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [showFilter, setShowFilter] = useState<boolean>(false)
  const [totalPages, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)
  const [isUpdate, setIsUpdate] = useState<boolean>(true)
  const {
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    activeFilter,
    applyFilter,
    resetToDefault,
    resetPageForSearch,
    isFilterActive,
  } = useAdminListState<UserFilterState>({
    storageKey: 'user-management',
    getDefaultFilter: getDefaultUserFilter,
    isDefaultFilter: (f, search) =>
      f.preset === 'allTime' &&
      !f.profession &&
      f.role == null &&
      !search.trim(),
  })
  const [checkedUsers, setCheckedUsers] = useState<any[]>([])
  const navigate = useNavigate()

  const isAllChecked = users.length > 0 && checkedUsers.length === users.length

  const handleHeaderCheckboxChange = (e: any) => {
    if (e.target.checked) {
      // Check all users
      const allUserIds = users.map((user) => user.id)
      setCheckedUsers(allUserIds)
    } else {
      // Uncheck all users
      setCheckedUsers([])
    }
  }

  // Handle individual checkbox change
  const handleCheckboxChange = (userId: any) => {
    setCheckedUsers(
      (prevChecked) =>
        prevChecked.includes(userId)
          ? prevChecked.filter((id) => id !== userId) // Uncheck
          : [...prevChecked, userId] // Check
    )
  }

  const toggleFilter = () => setShowFilter(!showFilter)

  const fetchUserManagementData = async (query: string) => {
    setIsUpdate(false)
    setIsLoading(true)
    try {
      const bodyData: Record<string, unknown> = {
        profession: activeFilter.profession ?? '',
        search: query,
        role: activeFilter.role ?? null,
        page,
        type: '',
      }
      appendDateFilterToBody(bodyData, activeFilter)
      const response = await restRequest.getUsersManagementList(bodyData)
      setUsers(response?.data?.data?.data ?? [])
      setTotalPages(response?.data?.data?.pager?.totalPages ?? 0)
      setTotalRecords(response?.data?.data?.pager?.totalRecords ?? 0)
    } catch (error) {
      console.error('Failed to load users:', error)
      setUsers([])
      setTotalPages(0)
      setTotalRecords(0)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUserManagementData(searchQuery)
  }, [isUpdate, page, activeFilter])

  const deleteUser = async (id: any) => {
    if (window.confirm('Are you sure, you want delete user')) {
      let resp: any = await restRequest.deleteUserData(id)
      if (resp.data) {
        setIsUpdate(true)
      }
    }
  }

  const toggleUserStatus = async (user: any) => {
    const newStatus = user.is_active ? false : true

    try {
      // Send update to backend
      let response = await restRequest.updateUserStatus({
        user_id: user.id,
        status: newStatus,
      })
      if (response?.data.code === 200) {
        // console.log(response?.data?.message)
        toast.success(response?.data?.message)
        setIsUpdate(true)
        setIsLoading(false)
      }

      // Optimistically update UI
      // setUsers((prevUsers) =>
      //   prevUsers.map((u) => (u.id === user.id ? {...u, activity_status: newStatus} : u))
      // )
    } catch (error) {
      console.error('Failed to update user status:', error)
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (e.target.value === '') {
      resetPageForSearch()
      fetchUserManagementData('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      resetPageForSearch()
      fetchUserManagementData(searchQuery)
    }
  }

  const submitSearch = () => {
    resetPageForSearch()
    fetchUserManagementData(searchQuery)
  }

  const handleExportUsers = () => {
    const payload: ExportReportPayload = {type: 'user'}
    appendDateFilterToExport(payload, activeFilter)
    if (activeFilter.profession) payload.profession = activeFilter.profession
    if (activeFilter.role != null) payload.role = activeFilter.role
    if (searchQuery?.trim()) payload.search = searchQuery.trim()
    runExport(payload)
  }

  const filterSummary = buildUserFilterSummary(activeFilter, searchQuery, totalRecords)

  // const downloadExportFile = async () => {
  //   let exportData = {
  //     type: 'user',
  //     month: currentMonth,
  //   }

  //   let response = await axios.post(`${apiUrl}/admin/report`, exportData, {
  //     headers: {'x-access-token': token.JWToken},
  //     responseType: 'blob',
  //   })

  //   console.log(response)

  //   const url = window.URL.createObjectURL(new Blob([response.data]))
  //   const link = document.createElement('a')
  //   link.href = url
  //   link.setAttribute('download', 'data.xls') // 设置下载文件名
  //   document.body.appendChild(link)
  //   link.click()
  //   document.body.removeChild(link)
  //   window.URL.revokeObjectURL(url)
  // }

  // const getPageNumbers = () => {
  //   let pages = []
  //   if (totalPages <= 5) {
  //     for (let i = 1; i <= totalPages; i++) {
  //       pages.push(i)
  //     }
  //   } else {
  //     if (page <= 3) {
  //       pages = [1, 2, 3, 4, 5, '...', totalPages]
  //     } else if (page >= totalPages - 2) {
  //       pages = [
  //         1,
  //         '...',
  //         totalPages - 4,
  //         totalPages - 3,
  //         totalPages - 2,
  //         totalPages - 1,
  //         totalPages,
  //       ]
  //     } else {
  //       pages = [1, '...', page - 1, page, page + 1, '...', totalPages]
  //     }
  //   }
  //   return pages
  // }

  return (
    <div>
      <ToastContainer autoClose={3000} />
      <p className='card-label fw-bold fs-3 mb-2'>User Management</p>

      <ListFilterToolbar
        filterSummary={filterSummary}
        showReset={isFilterActive}
        onReset={resetToDefault}
        resetLabel='Reset filters'
      />

      {isLoading ? (
        <div className=' d-flex justify-content-center mt-10 align-items-center'>
          <div className='spinner-border text-primary'></div>
        </div>
      ) : (
        <div className={`card `}>
          <ListPageHeader
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onSearchKeyDown={handleKeyDown}
            onSearchSubmit={submitSearch}
            searchPlaceholder='Search users by name'
            isLoading={isLoading}
            isFilterActive={isFilterActive}
            onOpenFilter={() => setShowFilter(true)}
            onExport={handleExportUsers}
            exporting={exporting}
          />
          <div className='card-body py-3'>
            <div className='table-responsive'>
              {users?.length > 0 ? (
                <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      {/* <th className='w-25px'>
                        <div className='form-check form-check-sm form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            value='1'
                            data-kt-check='true'
                            data-kt-check-target='.widget-13-check'
                            checked={isAllChecked}
                            onChange={handleHeaderCheckboxChange}
                          />
                        </div>
                      </th> */}
                      <th className='min-w-80px'>Profile Pic</th>
                      <th className='min-w-140px'>Name</th>
                      <th className='min-w-120px'>Email</th>
                      <th className='min-w-120px'>Role</th>
                      <th className='min-w-120px'>Profession</th>
                      <th className='min-w-140px'>Subscription</th>
                      <th className='min-w-120px'>Activity Status</th>
                      <th className='min-w-120px'> Daily Earning</th>
                      <th className='min-w-120px'> Points Balance</th>
                      <th className='min-w-100px text-end'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((item: any, index: number) => {
                      return (
                        <tr key={index}>
                          {/* <td>
                            <div className='form-check form-check-sm form-check-custom form-check-solid'>
                              <input
                                className='form-check-input widget-13-check'
                                type='checkbox'
                                value='1'
                                checked={checkedUsers.includes(item.id)}
                                onChange={() => handleCheckboxChange(item.id)}
                              />
                            </div>
                          </td> */}
                          <td className='p-2'>
                            <img
                              src={profilePictureSrc(item.profile_picture)}
                              alt={item.name || 'User'}
                              width={45}
                              height={45}
                              className='rounded-circle object-fit-cover'
                              onError={(e) => {
                                e.currentTarget.onerror = null
                                e.currentTarget.src = avatarFallback
                              }}
                            />
                          </td>
                          <td>
                            <Link
                              to={`/user-management/${item.id}`}
                              className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'
                            >
                              {item.name}
                            </Link>
                          </td>
                          <td>
                            <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                              {item.email}
                            </p>
                          </td>
                          <td>
                            <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                              {/* {item.role === 2
                                ? ' Driver'
                                : item.role === 3
                                ? ' Passenger'
                                : item.role === 4
                                ? ' Both'
                                : ' -'} */}
                              {item.role ? item.role : '-'}
                            </p>
                          </td>
                          <td className='text-dark fw-bold text-hover-primary fs-6'>
                            {item.profession}
                          </td>
                          <td>{subscriptionBadge(item.subscription_category)}</td>
                          <td>{activityStatusBadge(item.activity_status)}</td>
                          <td>
                            <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                              {item.car?.threshold}
                            </p>
                          </td>{' '}
                          <td>
                            <p className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>
                              {item.total_points}
                            </p>
                            {/* <span className='text-muted fw-semibold text-muted d-block fs-7'>
                          Code: PH
                        </span> */}
                          </td>
                          <td className='text-end d-flex align-items-center'>
                            <div className='form-check form-switch me-5'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                role='switch'
                                id={`flexSwitch-${item.id}`}
                                checked={item.is_active ? true : false}
                                onChange={() => toggleUserStatus(item)}
                              />
                              <label className='form-check-label' htmlFor={`flexSwitch-${item.id}`}>
                                Active
                              </label>
                            </div>
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                              onClick={() => navigate(`/user-management/${item.id}`)}
                            >
                              <KTIcon iconName='eye' className='fs-3' />
                              {/* <MdRemoveRedEye className='fs-3 ' /> */}
                            </button>
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                              onClick={() => deleteUser(item.id)}
                            >
                              <KTIcon iconName='trash' className='fs-3' />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              ) : (
                <p className='fs-5 pt-3 text-center '>User are not found</p>
              )}
            </div>
          </div>
        </div>
      )}

      {totalPages > 1 && (
              // <nav aria-label='Page navigation'>
              //   <ul className='pagination mt-5'>
              //     <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
              //       <button className='page-link' onClick={() => setPage(page - 1)}>
              //         Previous
              //       </button>
              //     </li>
              //     {getPageNumbers().map((page, index) => (
              //       <li
              //         key={index}
              //         className={`page-item ${page === page ? 'active' : ''} ${
              //           page === '...' ? 'disabled' : ''
              //         }`}
              //       >
              //         <button
              //           className='page-link'
              //           onClick={() => typeof page === 'number' && setPage(page)}
              //         >
              //           {page}
              //         </button>
              //       </li>
              //     ))}
              //     <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
              //       <button className='page-link' onClick={() => setPage(page + 1)}>
              //         Next
              //       </button>
              //     </li>
              //   </ul>
              // </nav>
        <Pagination totalPages={totalPages} page={page} setPage={setPage} />
      )}

      <FilterUsers
        showFilter={showFilter}
        toggle={toggleFilter}
        activeFilter={activeFilter}
        onApply={applyFilter}
      />
    </div>
  )
}

export default UserManagementWrapper
